import React from 'react';

import { usePageStore } from '@client/store/pageStore/context/pageContext';
import lediumApi from '@client/utils/lediumApi';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { Alert, InputBaseComponentProps, TextField, Typography } from '@mui/material';
import { styled } from '@mui/system';
import { TreeItem, TreeView } from '@mui/x-tree-view';

const Root = styled('div')(
    () => `
  ul {
    overflow: hidden;
  }
`
);

export interface GroupItem {
    id: string;
    name: string;
    description?: string;
    parent?: string;
    locked?: string;
    linked?: string;
}

interface GroupTreeNode extends GroupItem {
    children: GroupTreeNode[];
}

interface OutlinedDivProps {
    children: React.ReactNode;
    label?: React.ReactNode;
}

// eslint-disable-next-line
const InputComponent = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
    (props, ref) => <div {...props} ref={ref} />
);

const OutlinedDiv = ({ children, label }: OutlinedDivProps) => (
    <TextField
        variant="outlined"
        label={label}
        multiline
        fullWidth
        InputLabelProps={{ shrink: true }}
        InputProps={{
            inputComponent: InputComponent as unknown as React.ComponentType<InputBaseComponentProps>
        }}
        inputProps={{ children: children }}
    />
);

const openAllOnInitThreshold = 20;

interface SelectGroupTreeProps {
    label?: React.ReactNode;
    itemList?: GroupItem[];
    defaultSelected?: string;
    onSelect?: (item: GroupItem | undefined) => void;
    onEdit?: (item: GroupItem) => void;
    denySelectBelow?: string;
    disabledMessage?: React.ReactNode;
    disabled?: boolean;
}

const SelectGroupTree = React.memo(
    ({ label, itemList, defaultSelected, onSelect, onEdit, denySelectBelow, disabledMessage = null, disabled = false }: SelectGroupTreeProps) => {
        const {
            pageState: { formData }
        } = usePageStore();
        const [groups, setGroups] = React.useState<GroupItem[]>([]);
        const [groupData, setGroupData] = React.useState<Partial<GroupTreeNode>>({});
        const [expanded, setExpanded] = React.useState<string[]>([]);
        const [selected, setSelected] = React.useState<string>('');

        const buildTree = React.useCallback((groupItems: GroupItem[]) => {
            const root: GroupItem = {
                id: 'root',
                name: 'Root category',
                linked: 'ROOT_CATEGORY',
                locked: 'ROOT_CATEGORY'
            };
            if (!groupItems.find((g) => g.id === 'root')) {
                groupItems.unshift(root);
            }

            const hashTable: Record<string, GroupTreeNode> = Object.create(null);
            groupItems.forEach((aData) => {
                hashTable[aData.id] = { ...aData, children: [] };
            });
            const dataTree: GroupTreeNode[] = [];
            groupItems.forEach((aData) => {
                if (aData.parent && hashTable[aData.parent]) {
                    hashTable[aData.parent].children.push(hashTable[aData.id]);
                } else {
                    dataTree.push(hashTable[aData.id]);
                }
            });
            setGroupData(dataTree.shift() ?? {});
        }, []);

        const searchTree = React.useCallback(
            (searchId: string, struct: Partial<GroupTreeNode> = groupData): string[] | null => {
                if (struct.id === searchId) {
                    return [];
                } else if (struct.children) {
                    for (const child of struct.children) {
                        const path = searchTree(searchId, child);
                        if (path !== null) {
                            path.unshift(child.id);
                            return path;
                        }
                    }
                }
                return null;
            },
            [groupData]
        );

        function handleToggle(event: React.SyntheticEvent, nodeIds: string[]) {
            setExpanded(nodeIds);
        }

        function handleSelect(event: React.SyntheticEvent, nodeIds: string | string[]) {
            const nodeId = Array.isArray(nodeIds) ? nodeIds[0] : nodeIds;
            if (denySelectBelow) {
                const pathToSelected = searchTree(nodeId);
                if (pathToSelected && pathToSelected.includes(denySelectBelow)) {
                    return null;
                }
            }

            setSelected(nodeId);
            if (typeof onSelect === 'function') {
                const selectedItem = groups.find((item) => item.id === nodeId);
                onSelect(selectedItem);
            }
        }

        const setGroupItems = React.useCallback(
            (groupsData: GroupItem[]) => {
                setGroups(groupsData);
                buildTree(groupsData);
                if (onSelect && defaultSelected) {
                    const selectedItem = groupsData.find((item) => item.id === defaultSelected);
                    onSelect(selectedItem);
                }
                // eslint-disable-next-line
    }, [buildTree, defaultSelected]);

        React.useEffect(() => {
            if (itemList) {
                setGroupItems(itemList);
            } else {
                lediumApi.get<GroupItem[]>('groups').then((groupsData) => groupsData && setGroupItems(groupsData));
            }
        }, [itemList, setGroupItems]);

        React.useEffect(() => {
            if (groupData.id && defaultSelected) {
                if (groups && groups.length > openAllOnInitThreshold) {
                    const tree = searchTree(defaultSelected) || [];
                    setExpanded(['root', ...tree]);
                } else {
                    setExpanded(groups.map((g) => g.id));
                }
                setSelected(defaultSelected);
            }
        }, [searchTree, defaultSelected, groupData]);

        const renderTree = (nodes: GroupTreeNode): React.ReactElement => (
            <TreeItem
                key={nodes.id}
                nodeId={nodes.id}
                label={
                    <div style={{ padding: '4px 0' }} onDoubleClick={() => onEdit && nodes.id !== 'root' && onEdit(nodes)}>
                        <Typography display="inline" variant="body1" sx={{ fontSize: '16px', fontWeight: 500 }}>
                            {nodes.name}
                        </Typography>
                        {nodes.description && (
                            <Typography display="inline" variant="body2" sx={{ fontSize: '13px', marginLeft: '5px' }}>
                                ({nodes.description})
                            </Typography>
                        )}
                    </div>
                }
            >
                {Array.isArray(nodes.children) ? nodes.children.map((node) => renderTree(node)) : null}
            </TreeItem>
        );

        if (!groupData.id) {
            return null;
        }
        return (
            <Root>
                <OutlinedDiv label={label}>
                    {disabledMessage && (
                        <Alert sx={{ marginBottom: '8px' }} severity="warning">
                            {disabledMessage}
                        </Alert>
                    )}
                    <TreeView
                        defaultCollapseIcon={<ExpandMoreIcon />}
                        defaultExpanded={['root']}
                        defaultExpandIcon={<ChevronRightIcon />}
                        expanded={expanded}
                        selected={selected}
                        disableSelection={disabled || !!formData.readOnly}
                        disabledItemsFocusable
                        onNodeToggle={handleToggle}
                        onNodeSelect={handleSelect}
                    >
                        {renderTree(groupData as GroupTreeNode)}
                    </TreeView>
                </OutlinedDiv>
            </Root>
        );
    }
);

SelectGroupTree.displayName = 'SelectGroupTree';
export { SelectGroupTree };
